import { useForm } from '@mantine/form';
import {
  Modal,
  TextInput,
  Stack,
  Button,
  NumberInput,
  Select,
  Group,
  Box,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { 
  AccountType, 
  AccountSubtype, 
  AccountTypeCategory, 
  DepositorySubtype, 
  CreditSubtype, 
  LoanSubtype, 
  InvestmentSubtype 
} from '../types/account';

interface AccountFormData {
  name: string;
  account_type: AccountType;
  account_subtype?: AccountSubtype;
  current_balance: number;
  currency_code: string;
  mask?: string;
}

interface AccountModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (values: AccountFormData) => Promise<void>;
}

export const AccountModal = ({
  opened,
  onClose,
  onSubmit,
}: AccountModalProps) => {
  const [subtypes, setSubtypes] = useState<{ value: string; label: string; }[]>([]);

  const form = useForm<AccountFormData>({
    initialValues: {
      name: '',
      account_type: 'checking',
      account_subtype: undefined,
      current_balance: 0,
      currency_code: 'USD',
      mask: '',
    },
    validate: {
      name: (value) => (!value ? 'Name is required' : null),
      account_type: (value) => (!value ? 'Account type is required' : null),
      current_balance: (value) => (value === undefined ? 'Balance is required' : null),
    },
  });

  // Update subtypes when account type changes
  useEffect(() => {
    const accountType = form.values.account_type;
    let newSubtypes: { value: string; label: string; }[] = [];
    
    // Clear subtype when account type changes
    form.setFieldValue('account_subtype', undefined);

    // Map account types to subtype options
    switch (accountType) {
      case 'depository':
        newSubtypes = [
          { value: 'checking', label: 'Checking' },
          { value: 'savings', label: 'Savings' },
          { value: 'hsa', label: 'Health Savings Account (HSA)' },
          { value: 'cd', label: 'Certificate of Deposit' },
          { value: 'money market', label: 'Money Market' },
          { value: 'paypal', label: 'PayPal' },
          { value: 'prepaid', label: 'Prepaid' },
          { value: 'cash management', label: 'Cash Management' },
          { value: 'ebt', label: 'EBT' },
        ];
        break;
      case 'credit':
        newSubtypes = [
          { value: 'credit card', label: 'Credit Card' },
          { value: 'paypal', label: 'PayPal Credit' },
        ];
        break;
      case 'loan':
        newSubtypes = [
          { value: 'auto', label: 'Auto Loan' },
          { value: 'business', label: 'Business Loan' },
          { value: 'commercial', label: 'Commercial Loan' },
          { value: 'construction', label: 'Construction Loan' },
          { value: 'consumer', label: 'Consumer Loan' },
          { value: 'home equity', label: 'Home Equity Loan' },
          { value: 'loan', label: 'Generic Loan' },
          { value: 'mortgage', label: 'Mortgage' },
          { value: 'overdraft', label: 'Overdraft' },
          { value: 'line of credit', label: 'Line of Credit' },
          { value: 'student', label: 'Student Loan' },
          { value: 'other', label: 'Other Loan' },
        ];
        break;
      case 'investment':
        newSubtypes = [
          { value: 'brokerage', label: 'Brokerage' },
          { value: '401k', label: '401(k)' },
          { value: '403B', label: '403(b)' },
          { value: '457b', label: '457(b)' },
          { value: '529', label: '529 Plan' },
          { value: 'ira', label: 'IRA' },
          { value: 'roth', label: 'Roth IRA' },
          { value: 'other', label: 'Other Investment' },
        ];
        break;
      // Support legacy types
      case 'checking':
        form.setFieldValue('account_subtype', 'checking');
        newSubtypes = [{ value: 'checking', label: 'Checking' }];
        break;
      case 'savings':
        form.setFieldValue('account_subtype', 'savings');
        newSubtypes = [{ value: 'savings', label: 'Savings' }];
        break;
      case 'credit_card':
        form.setFieldValue('account_subtype', 'credit card');
        newSubtypes = [{ value: 'credit card', label: 'Credit Card' }];
        break;
      default:
        newSubtypes = [{ value: 'other', label: 'Other' }];
        form.setFieldValue('account_subtype', 'other');
    }
    
    setSubtypes(newSubtypes);
  }, [form.values.account_type]);

  const handleSubmit = async (values: AccountFormData) => {
    try {
      await onSubmit({
        ...values,
        mask: values.mask || undefined,
      });
      form.reset();
      onClose();
      notifications.show({
        title: 'Success',
        message: 'Account created successfully',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to create account',
        color: 'red',
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Add Manual Account"
      centered
      size="md"
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <TextInput
            label="Account Name"
            placeholder="e.g., My Checking Account"
            required
            {...form.getInputProps('name')}
          />

          <Select
            label="Account Type"
            placeholder="Select account type"
            required
            data={[
              { group: 'Standard Account Types', items: [
                { value: 'checking', label: 'Checking' },
                { value: 'savings', label: 'Savings' },
                { value: 'credit_card', label: 'Credit Card' },
              ]},
              { group: 'Plaid Account Types', items: [
                { value: 'depository', label: 'Depository' },
                { value: 'credit', label: 'Credit' },
                { value: 'loan', label: 'Loan' },
                { value: 'investment', label: 'Investment' },
                { value: 'other', label: 'Other' },
              ]}
            ]}
            {...form.getInputProps('account_type')}
          />

          {/* Show subtype select when there are subtypes available */}
          {subtypes.length > 1 && (
            <Select
              label="Account Subtype"
              placeholder="Select account subtype"
              data={subtypes}
              {...form.getInputProps('account_subtype')}
            />
          )}

          <NumberInput
            label="Current Balance"
            placeholder="0.00"
            required
            decimalScale={2}
            fixedDecimalScale
            min={-999999999}
            max={999999999}
            {...form.getInputProps('current_balance')}
          />

          <Select
            label="Currency"
            placeholder="Select currency"
            required
            data={[
              { value: 'USD', label: 'USD' },
              { value: 'EUR', label: 'EUR' },
              { value: 'GBP', label: 'GBP' },
            ]}
            {...form.getInputProps('currency_code')}
          />

          <TextInput
            label="Account Mask (last 4 digits)"
            placeholder="e.g., 1234"
            maxLength={4}
            {...form.getInputProps('mask')}
          />

          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose}>Cancel</Button>
            <Button type="submit">Add Account</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default AccountModal; 